import { makeStyles } from "@mui/styles";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { useMemo } from "react";
import { E3M3_TO_MCF, M3_TO_BBL, MONTH_LENGTH } from "../helpers/conversionFactors";

const useStyles = makeStyles({
  root: {
    border: 0,
    color: 'text.primary',
    height: 'auto',
    width: '100%',
    overflow: 'hidden'
  },
  tableHeading: {
    marginLeft: 10,
    marginTop: 10,
  }
});

export const EconomicsReservesYearsDataTable = ({ economics }) => {

  // STYLES
  const classes = useStyles();
  const currency = economics?.settings?.currency || 'cad';

  const rows = useMemo(() => {
    try {
      let data = [];
      const forecast = economics?.forecastNet;

      const totalYears = economics?.cashflowNet?.[economics?.cashflowNet?.length - 1]?.year - economics?.cashflowNet?.[0]?.year + 1;
      const residualYears = totalYears - 20;

      economics.cashflowNet.forEach(cashflow => {
        const year = cashflow?.year;
        const oilRevenue = Number((economics?.cashflowByFluidNet?.byFluid?.oil?.find(c => c.year === year)?.grossRevenue / 1000)?.toFixed(2)) || 0;
        const gasRevenue = Number((
          economics?.cashflowByFluidNet?.byFluid?.salesGas?.find(c => c.year === year)?.grossRevenue / 1000 || 0
        )?.toFixed(2)) || 0;
        const otherRevenue = Number((
          (economics?.cashflowByFluidNet?.byFluid?.ngl?.find(c => c.year === year)?.grossRevenue +
          economics?.cashflowByFluidNet?.byFluid?.condensate?.find(c => c.year === year)?.grossRevenue) / 1000 || 0
        )?.toFixed(2)) || 0;

        data.push({
          id: year,
          year,
          oil: Number((forecast?.byFluid?.oil?.find(f => f.year === year)?.volume?.p50 * M3_TO_BBL / 12 / MONTH_LENGTH)?.toFixed(2)) || 0,
          gas: Number((forecast?.byFluid?.salesGas?.find(f => f.year === year)?.volume?.p50 * E3M3_TO_MCF / 12 / MONTH_LENGTH)?.toFixed(2)) || 0,
          boe: Number((cashflow?.boe / 12 / MONTH_LENGTH)?.toFixed(2)) || 0,
          oilRevenue: oilRevenue,
          gasRevenue: gasRevenue,
          otherRevenue: otherRevenue,
          totalCrown: 0,
          nonCrown: Number((cashflow?.royalty / 1000)?.toFixed(2)) || 0,
          opex: Number((cashflow?.costs / 1000)?.toFixed(2)) || 0,
          otherExp: 0,
          capex: Number((cashflow?.capex / 1000)?.toFixed(2)) || 0,
          adValorem: Number((cashflow?.adValorem / 1000)?.toFixed(2)) || 0,
          abandonment: Number((cashflow?.abandonment / 1000)?.toFixed(2)) || 0,
          cashflowBTax: Number((cashflow?.cashflowBTax / 1000)?.toFixed(2)) || 0,
          incomeTax: Number(((cashflow?.incomeTax) / 1000)?.toFixed(2)) || 0,
          cashflowATax: Number((cashflow?.cashflowATax / 1000)?.toFixed(2)) || 0
        });

      });

      const subtotal = {
        id: 'Subtotal',
        year: 'Subtotal',
        oil: null,
        gas: null,
        boe: null,
        oilRevenue: Number((data.slice(0, 20)?.reduce((tot, curr) => tot + curr.oilRevenue, 0))?.toFixed(2)) || 0,
        gasRevenue: Number((data.slice(0, 20)?.reduce((tot, curr) => tot + curr.gasRevenue, 0))?.toFixed(2)) || 0,
        otherRevenue: Number((data.slice(0, 20)?.reduce((tot, curr) => tot + curr.otherRevenue, 0))?.toFixed(2)) || 0,
        totalCrown: 0,
        nonCrown: Number((data.slice(0, 20)?.reduce((tot, curr) => tot + curr.nonCrown, 0))?.toFixed(2)) || 0,
        opex: Number((data.slice(0, 20)?.reduce((tot, curr) => tot + curr.opex, 0))?.toFixed(2)) || 0,
        otherExp: 0,
        capex: Number((data.slice(0, 20)?.reduce((tot, curr) => tot + curr.capex, 0))?.toFixed(2)) || 0,
        adValorem: Number((data.slice(0, 20)?.reduce((tot, curr) => tot + curr.adValorem, 0))?.toFixed(2)) || 0,
        abandonment: Number((data.slice(0, 20)?.reduce((tot, curr) => tot + curr.abandonment, 0))?.toFixed(2)) || 0,
        cashflowBTax: Number((data.slice(0, 20)?.reduce((tot, curr) => tot + curr.cashflowBTax, 0))?.toFixed(2)) || 0,
        incomeTax: Number((data.slice(0, 20)?.reduce((tot, curr) => tot + curr.incomeTax, 0))?.toFixed(2)) || 0,
        cashflowATax: Number((data.slice(0, 20)?.reduce((tot, curr) => tot + curr.cashflowATax, 0))?.toFixed(2)) || 0
      };

      const residual = {
        id: `${residualYears} Years`,
        year: `${residualYears} Years`,
        oil: null,
        gas: null,
        boe: null,
        oilRevenue: Number((data.slice(20, )?.reduce((tot, curr) => tot + curr.oilRevenue, 0))?.toFixed(2)) || 0,
        gasRevenue: Number((data.slice(20, )?.reduce((tot, curr) => tot + curr.gasRevenue, 0))?.toFixed(2)) || 0,
        otherRevenue: Number((data.slice(20, )?.reduce((tot, curr) => tot + curr.otherRevenue, 0))?.toFixed(2)) || 0,
        totalCrown: 0,
        nonCrown: Number((data.slice(20, )?.reduce((tot, curr) => tot + curr.nonCrown, 0))?.toFixed(2)) || 0,
        opex: Number((data.slice(20, )?.reduce((tot, curr) => tot + curr.opex, 0))?.toFixed(2)) || 0,
        otherExp: 0,
        capex: Number((data.slice(20, )?.reduce((tot, curr) => tot + curr.capex, 0))?.toFixed(2)) || 0,
        adValorem: Number((data.slice(20, )?.reduce((tot, curr) => tot + curr.adValorem, 0))?.toFixed(2)) || 0,
        abandonment: Number((data.slice(20, )?.reduce((tot, curr) => tot + curr.abandonment, 0))?.toFixed(2)) || 0,
        cashflowBTax: Number((data.slice(20, )?.reduce((tot, curr) => tot + curr.cashflowBTax, 0))?.toFixed(2)) || 0,
        incomeTax: Number((data.slice(20, )?.reduce((tot, curr) => tot + curr.incomeTax, 0))?.toFixed(2)) || 0,
        cashflowATax: Number((data.slice(20, )?.reduce((tot, curr) => tot + curr.cashflowATax, 0))?.toFixed(2)) || 0
      };

      const total = {
        id: 'Total',
        year: 'Total',
        oil: null,
        gas: null,
        boe: null,
        oilRevenue: Number((subtotal.oilRevenue + residual.oilRevenue)?.toFixed(2)) || 0,
        gasRevenue: Number((subtotal.gasRevenue + residual.gasRevenue)?.toFixed(2)) || 0,
        otherRevenue: Number((subtotal.otherRevenue + residual.otherRevenue)?.toFixed(2)) || 0,
        totalCrown: 0,
        nonCrown: Number((subtotal.nonCrown + residual.nonCrown)?.toFixed(2)) || 0,
        opex: Number((subtotal.opex + residual.opex)?.toFixed(2)) || 0,
        otherExp: 0,
        capex: Number((subtotal.capex + residual.capex)?.toFixed(2)) || 0,
        adValorem: Number((subtotal.adValorem + residual.adValorem)?.toFixed(2)) || 0,
        abandonment: Number((subtotal.abandonment + residual.abandonment)?.toFixed(2)) || 0,
        cashflowBTax: Number((subtotal.cashflowBTax + residual.cashflowBTax)?.toFixed(2)) || 0,
        incomeTax: Number((subtotal.incomeTax + residual.incomeTax)?.toFixed(2)) || 0,
        cashflowATax: Number((subtotal.cashflowATax + residual.cashflowATax)?.toFixed(2)) || 0
      };

      data = data.slice(0, 20);
      data.push(subtotal);
      data.push(residual);
      data.push(total);

      return data;

    } catch (error) {
    };
  }, [economics]);

  const columns = useMemo(() => {
    return [
      {
        field: 'year',
        headerName: 'Year',
        width: 75,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'oil',
        headerName: 'Oil Comp. Gross (bbl/d)',
        width: 100,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'gas',
        headerName: 'Gas Comp. Gross (Mscf/d)',
        width: 100,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'boe',
        headerName: 'BOE Comp. Gross (boe/d)',
        width: 100,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'oilRevenue',
        headerName: `Oil Revenue (M ${currency === 'cad' ? 'CAD' : 'USD'})`,
        width: 100,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'gasRevenue',
        headerName: `Gas Revenue (M ${currency === 'cad' ? 'CAD' : 'USD'})`,
        width: 100,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'otherRevenue',
        headerName: `Other Revenue (M ${currency === 'cad' ? 'CAD' : 'USD'})`,
        width: 100,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'totalCrown',
        headerName: `Total Crown (M ${currency === 'cad' ? 'CAD' : 'USD'})`,
        width: 100,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'nonCrown',
        headerName: `Non Crown (M ${currency === 'cad' ? 'CAD' : 'USD'})`,
        width: 100,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'opex',
        headerName: `OPEX (M ${currency === 'cad' ? 'CAD' : 'USD'})`,
        width: 100,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'otherExp',
        headerName: `Other Exp (M ${currency === 'cad' ? 'CAD' : 'USD'})`,
        width: 100,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'capex',
        headerName: `Total Capital (M ${currency === 'cad' ? 'CAD' : 'USD'})`,
        width: 100,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'adValorem',
        headerName: `Ad Valorem (M ${currency === 'cad' ? 'CAD' : 'USD'})`,
        width: 100,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'abandonment',
        headerName: `Aband. & Reclam. (M ${currency === 'cad' ? 'CAD' : 'USD'})`,
        width: 100,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'cashflowBTax',
        headerName: `Cash Flow b/ Tax (M ${currency === 'cad' ? 'CAD' : 'USD'})`,
        width: 100,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'incomeTax',
        headerName: `Income Tax (M ${currency === 'cad' ? 'CAD' : 'USD'})`,
        width: 100,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'cashflowATax',
        headerName: `Cash Flow a/ Tax (M ${currency === 'cad' ? 'CAD' : 'USD'})`,
        width: 100,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      }
    ];
  }, [currency]);

  try {

    if (rows) {
      return (
        <div className={classes.root}>
          <DataGrid
            rows={rows}
            columns={columns}
            rowsPerPageOptions={[10,25,50,100]}
            disableSelectionOnClick
            className={classes.table}
            components={{
              Toolbar: GridToolbar,
            }}
            sx={{fontSize: 12}}
            rowHeight={24}
            autoHeight={true}
          />
        </div>
      );
    } else {
      return <></>;
    };

  } catch (error) {
    return <></>
  };

};
